import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Clock, Loader2, Mail, MapPin, Phone, ArrowRight } from "lucide-react";
import { site, fullAddress } from "@/config/site";
import { useLeadForm } from "@/components/lead/LeadForm";
import { trackEvent } from "@/lib/tracking";
import { SEO, breadcrumbLd } from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";

export default function Contact() {
  const { openForm } = useLeadForm();
  const location = useLocation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    consentService: false,
    consentMarketing: false,
  });
  const [status, setStatus] = useState("idle");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid =
    form.name.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    form.message.trim() &&
    form.consentService;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || status === "sending") return;
    setStatus("sending");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        ...form,
        page: location.pathname,
      });
      trackEvent("contact_form_submit");
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;

  return (
    <>
      <SEO
        title="Contact My Blinds Company | Blinds & Shutters Birmingham"
        description={`Contact My Blinds Company in Birmingham — call ${site.phone.display}, email ${site.email} or send a message. Free home design visits across Birmingham.`}
        path="/contact"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      <section className="bg-warm-50 grain py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Eyebrow className="justify-center">Contact</Eyebrow>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-slate-900 tracking-tight leading-tight">
              Talk to a real person about your windows
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-600">
              Call, email or send us a message — or book your free home design
              visit and we'll come to you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <div className="space-y-6">
              <div className="rounded-2xl border border-warm-200 bg-white p-7 shadow-sm">
                <h2 className="font-serif text-2xl text-slate-900 mb-5">{site.legalName}</h2>
                <ul className="space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 mt-0 shrink-0 text-navy-800" />
                    <span className="text-slate-700">
                      {fullAddress}
                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-directions-link"
                        className="block mt-1 font-semibold text-navy-900 underline"
                      >
                        Get directions
                      </a>
                    </span>
                  </li>
                  <li>
                    <a
                      href={site.phone.href}
                      data-testid="contact-phone-link"
                      onClick={() => trackEvent("phone_click", { location: "contact_page" })}
                      className="flex items-center gap-3 text-slate-700 hover:text-navy-900 transition-colors"
                    >
                      <Phone className="h-5 w-5 shrink-0 text-navy-800" />
                      <span className="font-semibold">{site.phone.display}</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href={`mailto:${site.email}`}
                      data-testid="contact-email-link"
                      onClick={() => trackEvent("email_click", { location: "contact_page" })}
                      className="flex items-center gap-3 text-slate-700 hover:text-navy-900 transition-colors break-all"
                    >
                      <Mail className="h-5 w-5 shrink-0 text-navy-800" />
                      {site.email}
                    </a>
                  </li>
                </ul>
              </div>
              <div className="rounded-2xl border border-warm-200 bg-white p-7 shadow-sm">
                <h3 className="flex items-center gap-2 font-sans text-base font-semibold text-slate-900 mb-4">
                  <Clock className="h-5 w-5 text-navy-800" /> Opening hours
                </h3>
                <ul className="space-y-2 text-sm">
                  {site.hours.map((h) => (
                    <li key={h.days} className="flex justify-between gap-4 text-slate-700">
                      <span>{h.days}</span>
                      <span className="font-medium">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl bg-navy-950 grain relative p-7 text-white">
                <h3 className="font-serif text-2xl mb-2">Prefer we come to you?</h3>
                <p className="text-sm text-white/70 leading-relaxed mb-5">
                  Book a free home design visit — samples, measuring and a clear
                  quote, all at home.
                </p>
                <button
                  type="button"
                  data-testid="contact-book-visit-btn"
                  onClick={() => openForm({ source: "contact_page" })}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-navy-900 transition-all duration-300 hover:-translate-y-0.5"
                >
                  {site.primaryCTA} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="rounded-2xl border border-warm-200 bg-white p-7 sm:p-9 shadow-sm">
              <h2 className="font-serif text-2xl text-slate-900 mb-6">Send us a message</h2>
              {status === "sent" ? (
                <div data-testid="contact-form-success" className="rounded-2xl bg-warm-100 border border-warm-200 p-8 text-center">
                  <h3 className="font-serif text-2xl text-slate-900 mb-2">Message received</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Thank you — we've received your message and will reply as
                    soon as we can during opening hours. For anything urgent,
                    call us on {site.phone.display}.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" noValidate={false}>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="text-sm font-medium text-slate-700 block mb-2">Name *</label>
                      <input
                        id="contact-name"
                        data-testid="contact-name-input"
                        required
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        autoComplete="name"
                        className="w-full rounded-xl border border-warm-300 bg-white px-4 py-3.5 text-base focus:border-navy-900 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="text-sm font-medium text-slate-700 block mb-2">Phone (optional)</label>
                      <input
                        id="contact-phone"
                        data-testid="contact-phone-input"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        autoComplete="tel"
                        className="w-full rounded-xl border border-warm-300 bg-white px-4 py-3.5 text-base focus:border-navy-900 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="text-sm font-medium text-slate-700 block mb-2">Email *</label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      autoComplete="email"
                      className="w-full rounded-xl border border-warm-300 bg-white px-4 py-3.5 text-base focus:border-navy-900 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="text-sm font-medium text-slate-700 block mb-2">Message *</label>
                    <textarea
                      id="contact-message"
                      data-testid="contact-message-input"
                      required
                      rows={5}
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      className="w-full rounded-xl border border-warm-300 bg-white px-4 py-3.5 text-base focus:border-navy-900 focus:outline-none resize-none"
                    />
                  </div>
                  <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="contact-consent-service"
                      checked={form.consentService}
                      onChange={(e) => set("consentService", e.target.checked)}
                      className="mt-1 h-4 w-4 accent-[#0B1D3A]"
                    />
                    <span>
                      I'm happy for My Blinds Company to contact me about my
                      enquiry, as described in the{" "}
                      <a href="/privacy-policy" className="underline text-navy-800">Privacy Policy</a>. *
                    </span>
                  </label>
                  <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="contact-consent-marketing"
                      checked={form.consentMarketing}
                      onChange={(e) => set("consentMarketing", e.target.checked)}
                      className="mt-1 h-4 w-4 accent-[#0B1D3A]"
                    />
                    <span>I'd also like occasional offers and news (optional).</span>
                  </label>
                  {status === "error" && (
                    <p role="alert" data-testid="contact-form-error" className="text-sm text-red-700">
                      Something went wrong. Please try again, or call us on {site.phone.display}.
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="contact-form-submit"
                    disabled={!valid || status === "sending"}
                    className="inline-flex items-center gap-2 rounded-full bg-navy-900 px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-navy-900/10 transition-all duration-300 hover:bg-navy-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}
                    Send Message
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
